import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BarChart3, Clock, Info, Search, X } from 'lucide-react';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import WhatsAppStatsPeriodSelector from './WhatsAppStatsPeriodSelector';
import {
  buildStatsQueryParams,
  isCustomStatsReady,
  statsMessagesLabel,
  type WhatsAppStatsQuery,
} from './whatsappStatsPeriod';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import type {
  WhatsAppNotificationCatalog,
  WhatsAppNotificationEvent,
} from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import {
  fetchWhatsAppNotificationCatalog,
  fetchWhatsAppTemplateList,
  updateWhatsAppNotificationEvent,
} from '../../services/MasterAdmin/whatsappHubService';
import {
  WhatsAppNotificationToggleSwitch,
  WhatsAppNotificationRowActionsMenu,
  WhatsAppNotificationViewModal,
  WhatsAppNotificationTemplateEditModal,
  WhatsAppNotificationHealthCell,
  formatWhatsAppNotificationInr,
  resolveAssignedTemplateCategory,
  hasAssignableWhatsAppTemplate,
  assignedTemplateLabel,
  patchCatalogNotification,
  activeTemplateNameSet,
  buildActiveTemplateMap,
  matchesHealthFilter,
} from './WhatsAppNotificationsPanel';

function isRetargetEvent(item: Pick<WhatsAppNotificationEvent, 'eventKey'>) {
  return String(item.eventKey || '').startsWith('RETARGET_');
}

const WhatsAppRetargetingPanel: React.FC = () => {
  const [catalog, setCatalog] = useState<WhatsAppNotificationCatalog | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [statsQuery, setStatsQuery] = useState<WhatsAppStatsQuery>({ period: 'monthly' });
  const [service, setService] = useState('');
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState<'all' | 'untested' | 'working' | 'issues'>('all');
  const [viewItem, setViewItem] = useState<WhatsAppNotificationEvent | null>(null);
  const [templateItem, setTemplateItem] = useState<WhatsAppNotificationEvent | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [healthSavingKey, setHealthSavingKey] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const catalogLoadedRef = useRef(false);
  const staleDisableRef = useRef(new Set<string>());

  const activeNames = useMemo(() => activeTemplateNameSet(templates), [templates]);
  const activeTemplateByName = useMemo(() => buildActiveTemplateMap(templates), [templates]);

  const applyPatch = useCallback((eventKey: string, patch: Partial<WhatsAppNotificationEvent>) => {
    setCatalog((prev) => (prev ? patchCatalogNotification(prev, eventKey, patch) : prev));
    setViewItem((prev) => (prev?.eventKey === eventKey ? { ...prev, ...patch } : prev));
    setTemplateItem((prev) => (prev?.eventKey === eventKey ? { ...prev, ...patch } : prev));
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!isCustomStatsReady(statsQuery)) return;
    const silent = catalogLoadedRef.current;
    if (!silent) setCatalogLoading(true);
    try {
      const catalogRes = await fetchWhatsAppNotificationCatalog(buildStatsQueryParams(statsQuery));
      setCatalog(catalogRes.data);
      catalogLoadedRef.current = true;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load retargeting events'));
      if (!silent) setCatalog(null);
    } finally {
      if (!silent) setCatalogLoading(false);
    }
  }, [statsQuery]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const templatesRes = await fetchWhatsAppTemplateList();
      setTemplates(templatesRes.data || []);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (templatesLoading || !catalog) return;
    const candidates = catalog.items.filter(isRetargetEvent);
    const stale = candidates.filter(
      (item) => item.whatsappEnabled && !hasAssignableWhatsAppTemplate(item, activeNames)
    );
    const toFix = stale.filter((item) => !staleDisableRef.current.has(item.eventKey));
    if (!toFix.length) return;
    toFix.forEach((item) => staleDisableRef.current.add(item.eventKey));
    void (async () => {
      let disabled = 0;
      for (const item of toFix) {
        try {
          await updateWhatsAppNotificationEvent(item.eventKey, { whatsappEnabled: false });
          applyPatch(item.eventKey, { whatsappEnabled: false, enabled: false });
          disabled += 1;
        } catch {
          /* retry on next catalog refresh */
        } finally {
          staleDisableRef.current.delete(item.eventKey);
        }
      }
      if (disabled > 0) {
        toast.error(
          `WhatsApp turned off for ${disabled} retarget event${disabled === 1 ? '' : 's'} with a missing or unassigned template`
        );
      }
    })();
  }, [templatesLoading, catalog, activeNames, applyPatch]);

  const retargetItems = useMemo(() => {
    if (!catalog) return [];
    return catalog.items.filter(isRetargetEvent);
  }, [catalog]);

  const serviceOptions = useMemo(() => {
    const services = retargetItems.map((i) => i.service).filter(Boolean) as string[];
    return services.filter((s, index) => services.indexOf(s) === index).sort();
  }, [retargetItems]);

  const items = useMemo(() => {
    let list = retargetItems;
    if (service) list = list.filter((i) => i.service === service);
    if (healthFilter !== 'all') list = list.filter((i) => matchesHealthFilter(i, healthFilter));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (i) =>
          (i.pushTitle || i.label).toLowerCase().includes(q) ||
          i.sampleMessage.toLowerCase().includes(q) ||
          i.eventKey.toLowerCase().includes(q) ||
          i.service.toLowerCase().includes(q) ||
          (i.trigger || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [retargetItems, service, search, healthFilter]);

  const totals = useMemo(() => {
    const enabledEvents = retargetItems.filter((i) => i.whatsappEnabled).length;
    const totalMessages = retargetItems.reduce((sum, i) => sum + (i.stats?.billableCount || 0), 0);
    const estimatedCostInr = retargetItems.reduce(
      (sum, i) => sum + (i.stats?.estimatedCostInr || 0),
      0
    );
    return {
      events: retargetItems.length,
      enabledEvents,
      totalMessages,
      estimatedCostInr,
      qaNeeds: retargetItems.filter(
        (i) => matchesHealthFilter(i, 'untested') || matchesHealthFilter(i, 'issues')
      ).length,
    };
  }, [retargetItems]);

  const handleToggle = async (item: WhatsAppNotificationEvent, enabled: boolean) => {
    if (togglingKey === item.eventKey) return;

    if (enabled && !hasAssignableWhatsAppTemplate(item, activeNames)) {
      toast.error('Assign an approved template before enabling this retarget event');
      setTemplateItem(item);
      return;
    }

    const previous = item.whatsappEnabled;
    setTogglingKey(item.eventKey);
    applyPatch(item.eventKey, { whatsappEnabled: enabled, enabled });

    try {
      await updateWhatsAppNotificationEvent(item.eventKey, { whatsappEnabled: enabled });
      toast.success(enabled ? 'Retargeting enabled' : 'Retargeting disabled');
    } catch (err: unknown) {
      applyPatch(item.eventKey, { whatsappEnabled: previous, enabled: previous });
      toast.error(getApiErrorMessage(err, 'Toggle failed'));
    } finally {
      setTogglingKey(null);
    }
  };

  const handleHealthChange = async (
    item: WhatsAppNotificationEvent,
    patch: { templateWorking?: boolean | null; linkNavigationWorking?: boolean | null }
  ) => {
    const key = `${item.eventKey}:${patch.templateWorking !== undefined ? 'template' : 'link'}`;
    if (healthSavingKey === key) return;
    const previous = {
      templateWorking: item.templateWorking ?? null,
      linkNavigationWorking: item.linkNavigationWorking ?? null,
      templateHealthCheckedAt: item.templateHealthCheckedAt ?? null,
      linkHealthCheckedAt: item.linkHealthCheckedAt ?? null,
    };
    const next = {
      ...patch,
      ...(patch.templateWorking !== undefined
        ? {
            templateHealthCheckedAt:
              patch.templateWorking === null ? null : new Date().toISOString(),
          }
        : {}),
      ...(patch.linkNavigationWorking !== undefined
        ? {
            linkHealthCheckedAt:
              patch.linkNavigationWorking === null ? null : new Date().toISOString(),
          }
        : {}),
    };
    setHealthSavingKey(key);
    applyPatch(item.eventKey, next);
    try {
      await updateWhatsAppNotificationEvent(item.eventKey, patch);
    } catch (err: unknown) {
      applyPatch(item.eventKey, previous);
      toast.error(getApiErrorMessage(err, 'Failed to save health'));
    } finally {
      setHealthSavingKey(null);
    }
  };

  const filterClass =
    'w-full min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 sm:w-auto';

  return (
    <WhatsAppPanelShell
      title="Retargeting"
      description="Enable event-driven WhatsApp nudges, assign templates, mark QA health, and review delivery — messages send ~10 minutes after the user event via ReminderEngine."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <WhatsAppStatsPeriodSelector value={statsQuery} onChange={setStatsQuery} />
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            title="How retargeting runs"
            aria-label="How retargeting runs"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>
      }
    >
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs text-gray-500">Retarget events</p>
          <p className="text-xl font-bold">{totals.events}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs text-gray-500">Enabled</p>
          <p className="text-xl font-bold">{totals.enabledEvents}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs text-gray-500">{statsMessagesLabel(statsQuery)}</p>
          <p className="text-xl font-bold">{totals.totalMessages}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs text-gray-500">Est. cost</p>
          <p className="text-xl font-bold">{formatWhatsAppNotificationInr(totals.estimatedCostInr)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs text-gray-500">Needs QA</p>
          <p className="text-xl font-bold">{totals.qaNeeds}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search event, trigger, service…"
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
        </div>
        <select value={service} onChange={(e) => setService(e.target.value)} className={filterClass}>
          <option value="">All services</option>
          {serviceOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={healthFilter}
          onChange={(e) =>
            setHealthFilter(e.target.value as 'all' | 'untested' | 'working' | 'issues')
          }
          className={filterClass}
        >
          <option value="all">All QA health</option>
          <option value="untested">Untested</option>
          <option value="working">Template + link working</option>
          <option value="issues">Has issues</option>
        </select>
      </div>

      {catalogLoading && !catalog ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No retargeting events match your filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full min-w-[1120px] table-fixed text-sm">
            <thead className="bg-gray-50 text-left dark:bg-gray-800/80">
              <tr>
                <th className="w-[26%] px-4 py-2.5 font-medium">Event</th>
                <th className="w-[10%] px-4 py-2.5 font-medium">Service</th>
                <th className="w-[9%] px-4 py-2.5 font-medium">Type</th>
                <th className="w-[18%] px-4 py-2.5 font-medium">Template</th>
                <th className="w-[9%] px-4 py-2.5 font-medium">Enabled</th>
                <th className="w-[14%] px-4 py-2.5 font-medium">QA health</th>
                <th className="w-[10%] px-4 py-2.5 font-medium">Usage</th>
                <th className="w-[4%] px-2 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const displayTemplate = assignedTemplateLabel(item, activeNames);
                const canEnable = hasAssignableWhatsAppTemplate(item, activeNames);
                const templateCategory = resolveAssignedTemplateCategory(
                  item,
                  activeNames,
                  activeTemplateByName
                );
                return (
                  <tr key={item.eventKey} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 align-middle">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {item.pushTitle || item.label}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                        {item.trigger || item.sampleMessage}
                      </p>
                      <p className="mt-1 truncate font-mono text-[10px] text-gray-400">{item.eventKey}</p>
                    </td>
                    <td className="px-4 py-3 align-middle text-gray-600 dark:text-gray-400">
                      <span className="block truncate">{item.service}</span>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {templateCategory ? (
                        <span className="inline-block max-w-full truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-800">
                          {templateCategory}
                        </span>
                      ) : (
                        <span className="inline-block max-w-full truncate rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                          {item.category || 'MARKETING'}
                        </span>
                      )}
                    </td>
                    <td className="overflow-hidden px-4 py-3 align-middle">
                      <span
                        className={`block truncate text-xs ${
                          displayTemplate.missing
                            ? 'text-amber-700 dark:text-amber-300'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                        title={displayTemplate.text}
                      >
                        {displayTemplate.text}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 align-middle">
                      <WhatsAppNotificationToggleSwitch
                        checked={item.whatsappEnabled && canEnable}
                        disabled={
                          togglingKey === item.eventKey ||
                          templatesLoading ||
                          (!item.whatsappEnabled && !canEnable)
                        }
                        title={
                          !canEnable
                            ? 'Assign an approved template before enabling WhatsApp'
                            : undefined
                        }
                        onChange={(v) => void handleToggle(item, v)}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <WhatsAppNotificationHealthCell
                        item={item}
                        disabled={healthSavingKey?.startsWith(`${item.eventKey}:`)}
                        onChange={(patch) => void handleHealthChange(item, patch)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 align-middle text-xs">
                      <p>{item.stats.billableCount} sent</p>
                      <p className="text-gray-500">
                        {formatWhatsAppNotificationInr(item.stats.estimatedCostInr)}
                      </p>
                    </td>
                    <td className="px-2 py-3 align-middle">
                      <WhatsAppNotificationRowActionsMenu
                        item={item}
                        toggling={togglingKey === item.eventKey}
                        canEnable={canEnable}
                        onView={() => setViewItem(item)}
                        onTemplate={() => setTemplateItem(item)}
                        onToggle={(v) => void handleToggle(item, v)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewItem && (
        <WhatsAppNotificationViewModal
          item={viewItem}
          templates={templates}
          statsQuery={statsQuery}
          onStatsQueryChange={setStatsQuery}
          onClose={() => setViewItem(null)}
          onSaved={(patch) => {
            applyPatch(viewItem.eventKey, patch);
          }}
        />
      )}

      {templateItem && (
        <WhatsAppNotificationTemplateEditModal
          item={templateItem}
          templates={templates}
          onClose={() => setTemplateItem(null)}
          onSaved={(patch) => {
            applyPatch(templateItem.eventKey, patch);
            setTemplateItem(null);
          }}
        />
      )}

      {templatesLoading && templates.length === 0 && <p className="sr-only">Loading templates</p>}

      {infoOpen ? (
        <>
          <button
            type="button"
            aria-label="Close info"
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setInfoOpen(false)}
          />
          <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#128C7E]" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">How it runs</h3>
              </div>
              <button
                type="button"
                onClick={() => setInfoOpen(false)}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                Turn on an event and assign an Interakt template. Matching app analytics events enqueue a
                send after a <strong>10 minute</strong> delay. If the user books a doctor appointment before
                send time, the nudge is cancelled as converted. Cloud Scheduler hits ReminderEngine every
                minute (<code className="text-[11px]">POST /api/reminder-engine/run</code>) to drain the
                queue — no separate retarget cron job.
              </p>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                <strong>Specialty Search Abandoned</strong> fires when a patient picks a specialty in the
                online / in-clinic / instant search bar and does not complete a booking within ~10 minutes.
                The CTA opens the same consultation mode filtered by that specialty.
              </p>
              <Link
                to="/operations/whatsapp/analytics?focused=1&source=retargeting"
                onClick={() => setInfoOpen(false)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#128C7E] hover:underline"
              >
                <BarChart3 className="h-4 w-4" />
                View all retargeting analytics
              </Link>
            </div>
          </aside>
        </>
      ) : null}
    </WhatsAppPanelShell>
  );
};

export default WhatsAppRetargetingPanel;
